import type { HomeChartPoint } from "@/modules/home";
import { resolveChartColor } from "./chart-colors";

interface OverlayPluginParams {
  points: HomeChartPoint[];
  formatPointLabel: (value: number) => string;
  showPointLabels: boolean;
  markerIndex?: number;
  gridColor: string;
  showAxisLabels: boolean;
  showGrid: boolean;
}

/**
 * Os dois detalhes do protótipo que o Chart.js não desenha por conta própria: o
 * valor escrito acima de cada ponto do Capex e a linha vertical "Hoje" do
 * Financeiro. Ficam em um plugin local em vez de uma dependência a mais.
 */
export function createOverlayPlugin({
  points,
  formatPointLabel,
  showPointLabels,
  markerIndex,
  gridColor,
  showAxisLabels,
  showGrid,
}: OverlayPluginParams) {
  return {
    id: "home-chart-overlay",
    afterDatasetsDraw(chart: {
      ctx: CanvasRenderingContext2D;
      width: number;
      chartArea: { top: number; right: number; bottom: number; left: number };
      scales: {
        x: { ticks: unknown[]; getPixelForTick: (index: number) => number };
        y: { ticks: unknown[]; getPixelForTick: (index: number) => number };
      };
      getDatasetMeta: (index: number) => { data: { x: number; y: number }[] };
    }) {
      const meta = chart.getDatasetMeta(0);
      const { ctx, chartArea } = chart;
      if (!ctx || !meta?.data?.length) return;

      ctx.save();

      if (showGrid) {
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();

        if (showAxisLabels) {
          chart.scales.x.ticks.forEach((_, index) => {
            const x = chart.scales.x.getPixelForTick(index);
            if (x > chartArea.left && x < chartArea.right) {
              ctx.moveTo(x, chartArea.top);
              ctx.lineTo(x, chartArea.bottom);
            }
          });
        }

        chart.scales.y.ticks.forEach((_, index) => {
          const y = chart.scales.y.getPixelForTick(index);
          if (y > chartArea.top && y < chartArea.bottom) {
            ctx.moveTo(chartArea.left, y);
            ctx.lineTo(chartArea.right, y);
          }
        });

        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (showPointLabels) {
        ctx.fillStyle = resolveChartColor("text");
        ctx.font = "600 11px 'Fira Sans', sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";

        meta.data.forEach((element, index) => {
          const point = points[index];
          if (!point) return;

          const text = formatPointLabel(point.value);
          // Os pontos das pontas ficam sobre a borda do canvas: o texto é
          // trazido para dentro para não sair cortado.
          const half = ctx.measureText(text).width / 2 + 2;
          const x = Math.min(Math.max(element.x, half), chart.width - half);

          ctx.fillText(text, x, element.y - 8);
        });
      }

      const marker = markerIndex === undefined ? undefined : meta.data[markerIndex];
      if (marker) {
        ctx.strokeStyle = resolveChartColor("marker");
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(marker.x, chartArea.top);
        ctx.lineTo(marker.x, chartArea.bottom);
        ctx.stroke();

        ctx.fillStyle = resolveChartColor("text");
        ctx.font = "500 10px 'Fira Sans', sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText("Hoje", marker.x, chartArea.top - 2);
      }

      ctx.restore();
    },
  };
}
